namespace Solovis.UI.InputControls {
    
    export interface FormEntryOptions {        
        /**
         * name is a unique identifier
         */
        name: string;

        /**
         * human-readable label for the form label
         */
        label?: string;

        /**
         * inner html for the form label.  overrides label.
         */
        labelHtml?: string | JQuery | HTMLElement;

        /**
         * (standard help declaration object)
         */
        help?: any;

        /**
         * showError: (true/false to determine if standard error display mechanism should be used)
         */
        showError?: boolean;        

        /**
         * postSetup: function(formEntry) method called by FormEditor after all rows are added (this is actually handled by FormBuilder)
         */
        postSetup?: (formEntry: FormEntry) => void;

        /**
         * createEditor: function(createEditorOptions) that returns custom entryEditor
         */
        createEditor?: (createEditorOptions: FormCreateEditorOptions) => FormEntryEditorData;

        /**
         * Standard editor options (cannot be used in combination with createEditor).
         */
        editor?: FormEditorOptions;

        /**
         * isVisible is a function that determines if the entry should be visible. It will be called
         * immediately for each entry when formBuilder.setupDone is called, and it will automatically be
         * called after any formBuilder valueChanged notifications.
         */
        isVisible?: (formEntry: FormEntry) => boolean;

        initialValue?: any;

        /**
         * If true, show a link that will remove the entry from the FormBuilder.
         */
        showRemoveEntryLink?: boolean;

        /**
         * If true, show a link that will let user expand and collapse the entry.
         */
        showExpandCollapseLink?: boolean;

        /**
         * If true and showValueOnFullRow is true, hide the entire label element. We do not allow hiding
         * of the label unless we are using showValueOnFullRow mode.
         */
        hideLabelElement?: boolean;

        /**
         * If true, show the value editor on its own row.
         */
        showValueOnFullRow?: boolean;

        /**
         * Include border around value section.
         */
        showValueBorder?: boolean;

        /**
         * If true, initially show entry in collapsed state.
         */
        collapsed?: boolean;

        /**
         * If true, this is a group editor that should only have a label (and possibly help).
         */
        isGroupHeader?: boolean;

        /**
         * Apply the standard amount of top padding to container to have plain text align with the label (for the container used for custom createEditor).
         */
        applyStandardTextAlignTopPadding?: boolean;
    }

    export interface FormCreateEditorOptions {
        container: any;
        initialValue?: any;
        validationModel?: ValidationModel;
        formBuilder?: FormBuilder;
    }

    export interface FormEntryEditorData {
        getValue?: () => any;
        setValue?: (newValue: any) => void;

        /**
         * Optional method that can apply input focus to an inner element.
         */
        focus?: () => void;

        /**
         * Optional method that should run any necessary validation code and return true if the entry is in a valid state.
         */
        isValid?: () => boolean;

        /**
         * Optional method that can be used to resize the inner element.
         */
        setWidth?: (newWidth) => void;

        /**
         * Internal property only used by internals of FormEntry.
         */
        __isFormEntryEditorDataWrapper?: boolean;

        [propName: string]: any;
    }

    export interface FormEditorOptions {
        /**
         * Standard: text, bool, select, color, parameter, numberAsString, textarea, dayId, multiselect, utcTimestamp
         */
        type: string;

        checkBoxOptions?: FormCheckBoxOptions;
        textOptions?: FormEditorTextOptions;
        selectOptions?: FormEditorSelectOptions;
        multiselectOptions?: FormEditorMultiselectOptions;
        colorOptions?: FormEditorColorOptions;
        parameterOptions?: FormEditorParameterOptions;
        numberAsStringOptions?: FormEditorNumberAsStringOptions;
        integerOptions?: FormEditorIntegerOptions;
        textareaOptions?: FormEditorTextareaOptions;
        dayIdOptions?: FormEditorDayIdOptions;
        utcTimestampOptions?: FormEditorUtcTimestampOptions;

        required?: boolean;

        isValid?: (value: any, formEntry?: FormEntry, validationModel?: ValidationModel) => boolean;
    }


    export interface FormEditorSimpleDataSourceLoadOptions {
        searchValue?: string;
    }

    export interface FormEditorSimpleDataSource {
        /**
         * Data source must implement a load method that returns a Promise that resolves to the expected
         * type of "data" list for the editor.
         */
        load: (loadOptions: FormEditorSimpleDataSourceLoadOptions) => any;

        /**
         * Data source must implement a byKey method that returns a Promise that resolves to an expected
         * entry that matches "data" list for the editor.
         */
        byKey: (key: string) => any;
    }

    export interface FormEditorTextOptions {     
        width?: number | string;
        maxLength?: number;
        isPassword?: boolean;

        fireValueChangedOnKeyup?: boolean;
    }

    export interface FormEditorDayIdOptions {
        monthEnd?: boolean;
    }

    export interface FormCheckBoxOptions {
        disabled?: boolean;
    }

    export interface FormEditorSelectOptions {
        data?: { text: string, value: string, [propName: string]: any; }[];
        dataSource?: FormEditorSimpleDataSource;

        showClearButton?: boolean;
        searchEnabled?: boolean;

        /**
         * If set, allow user to type in custom values that don't match existing items.
         */
        acceptCustomValue?: boolean;
        /**
         * Callback to return an { text: "", value: "" } object or Promise that returns the same. Return false to cancel creation.
         */
        createCustomItemCallback?: (text: string) => any;
    }

    export interface FormEditorMultiselectOptions {
        data?: { text: string, value: string, [propName: string]: any; }[];   
        dataSource?: FormEditorSimpleDataSource;

        showClearButton?: boolean;
        searchEnabled?: boolean;

        /**
         * If set, allow user to type in custom values that don't match existing items.
         */
        acceptCustomValue?: boolean;     
        /**
         * Callback to return an { text: "", value: "" } object or Promise that returns the same. Return false to cancel creation.
         */
        createCustomItemCallback?: (text: string) => any;
    }

    export interface FormEditorColorOptions {
        editAlphaChannel?: boolean;
    }

    export interface FormEditorParameterOptions {
        parameterType?: string;

        /**
         * The settings that are passed into the parameter control.
         */
        settings?: any;

        /**
         * Optional override for FormBuilder-level portal services (for instance, if a parameter should be sourced from two different environments).
         */
        portalServices?: Solovis.Portal.IPortalServices;
    }

    export interface FormEditorNumberAsStringOptions {
        treatBlankAsZero?: boolean;

        /**
         * Number of digits allowed after the decimal.
         */
        decimals?: number;

        /**
         * Show a % sign as watermark on right edge of text entry (to signify % is being entered).
         */
        showPercentSign?: boolean;

        width?: number | string;

        fireValueChangedOnKeyup?: boolean;
    }

    export interface FormEditorIntegerOptions {
        treatBlankAsZero?: boolean;
        
        /**
         * Show a % sign as watermark on right edge of text entry (to signify % is being entered).
         */
        showPercentSign?: boolean;

        width?: number | string;

        fireValueChangedOnKeyup?: boolean;
    }

    export interface FormEditorTextareaOptions {
        width?: number | string;
        height?: number | string;
        trim?: boolean;
    }

    export interface FormEditorUtcTimestampOptions {
        displayMode?: "utc" | "local"
    }


    export class FormEntry {
        private defaultLabelColumnWidth = 150;

        public formBuilder: FormBuilder;
        public name: string;
        public entryEditor: any;
        public validationModel: ValidationModel;

        public _entryOptions: FormEntryOptions;

        public _label: string;
        public _labelHtml: string | JQuery | HTMLElement;
        public _help: any;
        public _showError: boolean;

        public _entryDiv: JQuery;
        public _labelDiv: JQuery;
        public _valueDiv: JQuery;

        public _labelElement: JQuery;
        public _errorDiv: JQuery;

        public _isFullRowForValue: boolean;

        public _validationModelControl: Solovis.UI.InputControls.ValidationModelControl;

        public _isCollapsed: boolean;
        public _expandSpan: JQuery;
        public _collapseSpan: JQuery;


        constructor(
            options: {
                formBuilder: Solovis.UI.InputControls.FormBuilder,
                entryOptions: FormEntryOptions
            }
        ) {
            let self = this;

            let formBuilder = options.formBuilder;
            this.formBuilder = formBuilder;

            let entryOptions = options.entryOptions;
            this._entryOptions = entryOptions;

            this._isCollapsed = !!entryOptions.collapsed;

            let name = entryOptions.name;
            let label = entryOptions.label;
            let labelHtml = entryOptions.labelHtml ? entryOptions.labelHtml : null;
            let help = entryOptions.help ? entryOptions.help : null;

            // We default to showError=true if it is not specified.
            let showError = Solovis.isNotNull(entryOptions.showError) ? entryOptions.showError : true;

            let createEditor = entryOptions.createEditor;
            if (!createEditor) {
                createEditor = (createEditorOptions: FormCreateEditorOptions) => {
                    return this._createStandardEntryEditor(entryOptions.editor, createEditorOptions);
                };
            }

            this._isFullRowForValue = !!entryOptions.showValueOnFullRow;

            let initialValue = null;
            if (typeof entryOptions.initialValue !== "undefined") {
                initialValue = entryOptions.initialValue;
            }
            else if (formBuilder._initialValues && (typeof formBuilder._initialValues[name] !== "undefined")) {
                initialValue = formBuilder._initialValues[name];
            }

            // NOTE: FormEntry is currently coupled to FormBuilder through the following line.
            let entryRow = $("<div>").addClass("formbuilder-entry").appendTo(formBuilder._formDiv);
            this._entryDiv = entryRow;

            let labelCellContainer = $("<div>").addClass("formbuilder-entry-label").appendTo(entryRow);
            this._labelDiv = labelCellContainer;
            labelCellContainer;
            if (entryOptions.showValueOnFullRow || entryOptions.isGroupHeader) {
                labelCellContainer.css("display", "block");
                labelCellContainer.addClass("formbuilder-entry-label-full-row");
                labelCellContainer.outerWidth(this.formBuilder.width());

                if (entryOptions.isGroupHeader) {
                    labelCellContainer.addClass("formbuilder-entry-label-full-row-group-header");
                }
            }
            else {
                if (formBuilder._options.labelColumnWidth) {
                    labelCellContainer.css("width", formBuilder._options.labelColumnWidth);
                }
                else {
                    labelCellContainer.css("width", this.defaultLabelColumnWidth);
                }
            }

            let labelCell = $("<div>");
            
            labelCell.appendTo(labelCellContainer);

            let labelElement = $("<label>").appendTo(labelCell);

            if (labelHtml) {
                (labelElement as any).html(labelHtml);
            }
            else {
                labelElement.text(label ? label : "");
            }

            if (help) {
                let helpSpan = $("<span>").text(" ").appendTo(labelCell);
                let helpLink = $("<a>").addClass("fa fa-question-circle").appendTo(helpSpan);
                helpLink.click(() => {
                    Solovis.Site.HelpManager.showCustomHelpDialog(
                        help,
                        {
                            title: self._label,
                            portalServices: this.formBuilder.portalServices()
                        });
                });
            }

            if (entryOptions.showRemoveEntryLink) {
                let removeSpan = $("<span>").text(" ").appendTo(labelCell);
                let removeLink = $("<a>").addClass("fa fa-remove").appendTo(removeSpan);
                removeLink.click(function () {
                    self.remove();
                });
            }

            if (entryOptions.showExpandCollapseLink) {
                let expandSpan = $("<span>").text(" ").appendTo(labelCell);
                let expandLink = $("<a>").addClass("fa fa-chevron-right").appendTo(expandSpan);
                expandLink.click(function () {
                    self.expand(true);
                });
                this._expandSpan = expandSpan;

                let collapseSpan = $("<span>").text(" ").appendTo(labelCell);
                let collapseLink = $("<a>").addClass("fa fa-chevron-down").appendTo(collapseSpan);
                collapseLink.click(function () {
                    self.expand(false);
                });
                this._collapseSpan = collapseSpan;

                this._updateExpandCollapseSpans();
            }            

            if (!entryOptions.isGroupHeader) {
                let valueCell = $("<div>").addClass("formbuilder-entry-value").appendTo(entryRow);
                this._valueDiv = valueCell;

                if (this._isCollapsed) {
                    this._valueDiv.addClass("formbuilder-entry-value-hidden");
                }

                if (entryOptions.showValueBorder) {
                    valueCell.addClass("formbuilder-entry-boxed-value");
                }

                if (entryOptions.showValueOnFullRow) {
                    valueCell.addClass("formbuilder-entry-value-full-row");
                    valueCell.outerWidth(this.formBuilder.width());

                    if (entryOptions.hideLabelElement) {
                        labelCellContainer.css("display", "none");
                    }
                }
                else {
                    let valueWidth = this._determineValueOuterWidth(this.formBuilder.width(), this._labelDiv.outerWidth());
                    valueCell.outerWidth(valueWidth);
                }

                let valueEditorDiv = $("<div>")
                    .addClass("formbuilder-entry-container")
                    .appendTo(valueCell);

                let validationModel = new Solovis.UI.InputControls.ValidationModel();

                let errorDiv = null;
                if (showError) {
                    errorDiv = $("<div>").appendTo(valueCell);
                    this._validationModelControl = new Solovis.UI.InputControls.ValidationModelControl({
                        controlHost: errorDiv,
                        validationModel: validationModel
                    });
                    this._validationModelControl.setAsCurrentControl();
                }

                // Create the editor.
                let createEditorOptions: FormCreateEditorOptions = {
                    formBuilder: formBuilder,
                    container: valueEditorDiv,
                    validationModel: validationModel,
                    initialValue: initialValue
                };

                if (entryOptions.applyStandardTextAlignTopPadding) {
                    createEditorOptions.container.css("padding-top", FormEntryFactoryManager.standardTextAlignTopPadding);
                }

                let entryEditor = createEditor(createEditorOptions);

                this.name = name;
                this.entryEditor = entryEditor;
                this.validationModel = validationModel;
                this._label = label;
                this._labelHtml = labelHtml;
                this._help = help;
                this._showError = showError;

                this._labelElement = labelElement;
                this._errorDiv = errorDiv;

                if (this.entryEditor) {
                    $(this.entryEditor).bind("valueChanged", function () { self._notifyValueChanged(); });
                }
            }
        }

        private _createStandardEntryEditor(editorOptions: FormEditorOptions, createEditorOptions: FormCreateEditorOptions): FormEntryEditorData {
            let self = this;

            if (!editorOptions) {
                return {
                    getValue: function () {
                        return null;
                    }
                };
            }

            let editor = this._internalCreateStandardEntryEditor(editorOptions, createEditorOptions);

            if (!editor.__isFormEntryEditorDataWrapper) {
                editor = FormEntryFactoryManager.WrapEditor(editor);
            }

            if (editorOptions.required || editorOptions.isValid) {
                let originalIsValid = editor.isValid;

                editor.isValid = function () {
                    if (originalIsValid) {
                        if (!originalIsValid()) {
                            // First check the underlying standard validation.
                            return false;
                        }
                    }

                    // The original validation passed, so let's see if the extra criteria are also met.
                    try {
                        let currentValue = editor.getValue();

                        if (editorOptions.required) {
                            if (Solovis.isNull(currentValue)) {
                                return false;
                            }
                        }

                        if (editorOptions.isValid) {
                            if (!editorOptions.isValid(currentValue, self, createEditorOptions.validationModel)) {
                                return false;
                            }
                        }
                    }
                    catch (ex) {
                        return false;
                    }

                    return true;
                };
            }

            return editor;
        };

        private _internalCreateStandardEntryEditor(editorOptions: FormEditorOptions, createEditorOptions: FormCreateEditorOptions): FormEntryEditorData {            

            let editorFactory = FormEntryFactoryManager.tryGetFactoryForType(editorOptions.type);
            if (editorFactory) {
                let customEditorData = editorFactory(editorOptions, createEditorOptions);
                return customEditorData;
            }            
            
            console.log("ERROR: FormEntry: unsupported type: " + editorOptions.type);

            return {
                getValue: function () {
                    return null;
                }
            };            
        }        


        public label(newLabel?: string) {
            if (!arguments.length) {
                return this._label;
            }
            else {
                this._label = newLabel;
                this._labelElement.text(this._label);
            }
        }

        public labelHtml(newLabelHtml: string | JQuery | HTMLElement) {
            if (!arguments.length) {
                return this._labelHtml;
            }
            else {
                this._labelHtml = newLabelHtml;
                (this._labelElement as any).html(this._labelHtml);
            }
        }

        public value(newValue?: any) {
            if (!arguments.length) {
                if (this.entryEditor && this.entryEditor.getValue) {
                    if (this.hasAsyncGetter()) {
                        let errorMessage = "FormEntry has async getter: " + this.name;
                        console.log(errorMessage);
                        throw errorMessage;
                    }

                    return this.entryEditor.getValue();
                }
                else {
                    return null;
                }
            }
            else {
                if (this.entryEditor && this.entryEditor.setValue) {
                    this.entryEditor.setValue(newValue);
                }
            }
        }

        public async getValueAsync(): Promise<any> {            
            let value = await this.entryEditor.getValueAsync();
            return value;            
        }

        public hasAsyncGetter(): boolean {
            if (this.entryEditor && this.entryEditor.getValueAsync) {
                return true;
            }

            return false;
        }        

        public isValid(): boolean {
            if (this.entryEditor && this.entryEditor.isValid && typeof this.entryEditor.isValid == "function") {
                var result = this.entryEditor.isValid();
                return result;
            }
            else {
                // If the entry editor does not have an isValid implementation, it is assumed that the value is valid.
                return true;
            }
        }

        public _determineValueOuterWidth(rowWidth, labelWidth) {
            let delta = rowWidth - labelWidth;
            if (delta < 100) {
                // We'll just use tableWidth to let the entry use the full next row.
                return rowWidth;
            }
            else {
                if (this._entryOptions.showValueBorder) {
                    // Account for the box margin.
                    delta -= 10;
                }

                return delta;
            }
        }

        public _updateWidth(newWidth) {
            if (this._entryOptions.isGroupHeader) {
                if (this._labelDiv) {
                    this._labelDiv.outerWidth(newWidth);
                }
            }
            else if (this._labelDiv && this._valueDiv) {
                if (this._isFullRowForValue) {
                    this._labelDiv.outerWidth(newWidth);
                    this._valueDiv.outerWidth(newWidth);
                }
                else {
                    let labelWidth = this._labelDiv.outerWidth();
                    let valueWidth = this._determineValueOuterWidth(newWidth, labelWidth);
                    this._valueDiv.outerWidth(valueWidth);

                    if (this.entryEditor.setWidth) {
                        let innerWidth = this._valueDiv.width();
                        this.entryEditor.setWidth(innerWidth);
                    }
                }
            }
        }

        public _updateVisibility() {
            if (this._entryOptions && this._entryOptions.isVisible) {
                try {
                    let isVisible = this._entryOptions.isVisible(this);
                    this.showEntry(isVisible);
                }
                catch (ex) {
                    console.log("ERROR: FormEntry._updateVisibility: entryName=" + this._entryOptions.name);
                    console.log(ex);
                }
            }
        }

        public focus() {            
            if (this.entryEditor && this.entryEditor.focus) {
                this.entryEditor.focus();
            }
        }


        public expand(isExpanded: boolean) {
            if (this._isCollapsed != !isExpanded) {
                if (isExpanded) {
                    this._isCollapsed = false;
                    if (this._valueDiv) {
                        this._valueDiv.removeClass("formbuilder-entry-value-hidden");                        
                    }
                }
                else {
                    this._isCollapsed = true;
                    if (this._valueDiv) {
                        this._valueDiv.addClass("formbuilder-entry-value-hidden");                        
                    }
                }

                this._updateExpandCollapseSpans();
            }
        }

        public _updateExpandCollapseSpans() {
            if (this._isCollapsed) {
                if (this._expandSpan) {
                    this._expandSpan.css("display", "inline");
                }
                if (this._collapseSpan) {
                    this._collapseSpan.css("display", "none");
                }
            }
            else {
                if (this._expandSpan) {
                    this._expandSpan.css("display", "none");
                }
                if (this._collapseSpan) {
                    this._collapseSpan.css("display", "inline");
                }
            }
        }


        /**
         * Remove the entry from the FormBuilder.
         */
        public remove(): void {
            if (this._entryDiv) {
                $(this._entryDiv).remove();
                this._entryDiv = null;

                this.formBuilder._internalRemoveEntryByName(this.name);
            }
        }


        public isGroupHeader(): boolean {
            return !!this._entryOptions.isGroupHeader;
        }


        /**
         * Change the visibility of the form entry.
         * @param visible
         */
        public showEntry(visible) {         
            $(this._entryDiv).toggle(visible ? true : false);
        };

        public _notifyValueChanged() {
            $(this).trigger("valueChanged", { formEntry: this });
        }

    }

}
